import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Role } from '../types';
import * as db from '../services/db';
import { usePos } from '../context/PosContext';
import '../pages/QrLogin.css';
import './LoginScreen.css';
import './DeviceMode.css';

// Terminal-mode picker. Shown full-screen on first launch (via the
// DeviceModeGate in App.tsx) so a new device starts on a limited menu instead
// of full admin, and also reachable later at /device-mode (drawer footer) to
// re-provision a device. The chosen role caps every menu on this device on top
// of whoever logs in (see db.canAccess / db.getDeviceRole). Picking an admin
// (system) role requires the admin PIN so a cashier can't self-promote a
// counter to full access.
const DeviceMode: React.FC<{ firstRun?: boolean }> = ({ firstRun }) => {
  const { config } = usePos();
  const navigate = useNavigate();
  const [roles, setRoles] = useState<Role[]>([]);
  const [current, setCurrent] = useState(db.getDeviceRole());
  const [error, setError] = useState('');

  useEffect(() => {
    db.getRoles().then(setRoles);
  }, []);

  // Friendly one-liners for the built-in roles; custom roles fall back to their
  // own name.
  const blurb = (r: Role): string => {
    if (r.system) return 'Full access — every menu (Manager / back office).';
    if (r.id === 'cashier') return 'Billing, customers, day reports — no masters or settings.';
    if (r.id === 'waiter') return 'Tables, Captain App and KOTs only.';
    return `${r.rights.length} menu${r.rights.length === 1 ? '' : 's'}.`;
  };

  const choose = async (role: Role) => {
    setError('');
    // Elevating a device to an admin (system) role needs the admin PIN.
    if (role.system) {
      const entered = window.prompt(
        `Set this device to "${role.name}" (full access).\nEnter the admin PIN to confirm:`
      );
      if (entered == null) return; // cancelled
      const users = await db.getUsers();
      const systemRoleIds = new Set(roles.filter((r) => r.system).map((r) => r.id));
      const ok = users.some(
        (u) => u.active && systemRoleIds.has(u.role) && u.pin === entered.trim()
      );
      if (!ok) {
        setError('Wrong admin PIN — this device was not changed.');
        return;
      }
    }
    await db.setDeviceRole(role.id);
    setCurrent(role.id);
    // On first launch the gate re-renders itself once the role is set; the
    // later "change" page returns to the dashboard.
    if (!firstRun) navigate('/dashboard');
  };

  return (
    <div className="ql-page dm-page">
      <div className="ql-card dm-card">
        <div className="ls-logo">EP</div>
        <div className="ls-outlet">{config.storeName || 'EPOS'}</div>
        <h2 className="dm-title">
          {firstRun ? 'What is this device?' : 'Change this device’s mode'}
        </h2>
        <p className="dm-sub">
          Choose which menus this device shows. You can change it later under
          {' '}the menu drawer.
        </p>

        {error && <div className="dm-error">{error}</div>}

        <div className="dm-list">
          {roles.map((r) => (
            <button
              key={r.id}
              className={`dm-option ${current === r.id ? 'dm-option--current' : ''}`}
              onClick={() => choose(r)}
            >
              <span className="dm-option-name">
                {r.name}
                {r.system && <span className="dm-lock" title="Requires admin PIN"> 🔒</span>}
                {current === r.id && <span className="dm-current-tag"> · current</span>}
              </span>
              <span className="dm-option-blurb">{blurb(r)}</span>
            </button>
          ))}
        </div>

        {!firstRun && (
          <button className="dm-back" onClick={() => navigate('/dashboard')}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default DeviceMode;
